<template>
  <section class="mt-2">
    <!-- Multi-dates calendar -->
    <div class="professionalMultiDatesCalendar datepicker-here"></div>
    <!-- Package type discount -->
    <div>
      <package-discount class="mt-4 ml-2" :numDates="numPackageSelectedDates" :packageType="packageType"/>
    </div>
    <!-- Selected date -->
    <section v-if="selectedDates">
      <div class="row mt-3" v-for="selectedDate, index in selectedDates">
        <div class="col-6 p-0 d-flex align-items-center">
          <span class="ml-2">
            <a href="" @click.prevent="removeDate(index, selectedDate)" class="text-primary-link">
              <i class="fal fa-trash-alt mr-2"></i>
            </a>
            <strong>{{ selectedDate | shortDate }}</strong>
          </span>
        </div>
        <div class="col-6 p-0">
          <professional-time-selection
            @selectTime="updateSelectedTime"
            :mapDatesTimes="mapDatesTimes"
            :selectedDate="selectedDate"
            :availableTimes="availableTimes[index]"
          />
        </div>
      </div>
    </section>
  </section>
</template>

<script>
  import { mapGetters } from 'vuex'
  import i18n from '../../helpers/i18nHelpers'
  import moment from 'moment-timezone'
  import axios from 'axios'
  import PackageDiscount from './newSteps/components/PackageDiscount'
  import ProfessionalTimeSelection from './ProfessionalTimeSelection'

  export default {
    props: {
      professionalId: String,
      maxNum: Number,
      workHours: String,
      packageType: Object
    },
    components: {
      PackageDiscount,
      ProfessionalTimeSelection
    },
    data () {
      return {
        language: 'en',
        selectedDates: [],
        datePicker: null,
        professionalCalendarJobs: null,
        availableTimes: [],
        fullBlockedDates: [],
        partialBlockedDates: [],
        cleaningDates: [],
        fullyBookedDates: [],
        mapDatesTimes: {},
        numPackageSelectedDates: 0
      }
    },
    computed: {
      ...mapGetters({
        globalState: 'globalVal/getState'
      })
    },
    watch: {
      'globalState.currentLanguage': function (newVal, oldVal) {
        this.datePicker.data('datepicker').update('language', newVal)
      },
      workHours () {
        this.mapDatesTimes = {}
        this.selectedDates = []
        this.datePicker.data('datepicker').clear()
        this.fetchSoldOutDates()
      }
    },
    methods: {
      updateSelectedTime (selectedDate, selectedTime) {
        const fmDate = moment(selectedDate).format('YYYY-MM-DD').toString()
        this.mapDatesTimes[fmDate] = selectedTime
        this.$emit('updateData', this.mapDatesTimes)
      },
      parseTime (s) {
        const c = s.split(':')
        return parseInt(c[0]) * 60 + parseInt(c[1])
      },
      removeDate (index, date) {
        const fmDate = moment(date).format('YYYY-MM-DD').toString()
        this.datePicker.data('datepicker').removeDate(date)
        this.availableTimes.splice(index, 1) // Remove time
        delete this.mapDatesTimes[fmDate]
      },
      isPartialBlockedDate (selectedDate) {
        const fmDate = moment(selectedDate).format('YYYY-MM-DD')
        for (let key in this.partialBlockedDates) {
          const bDate = this.partialBlockedDates[key]
          if (bDate.date === fmDate) {
            return {
              startTime: bDate.start_time,
              endTime: bDate.end_time
            }
          }
        }
        return null
      },
      getAvailableTimes (selectedDate) {
        let startTime = null
        let endTime = null
        let availableTimes = []
        const partialBlockedDate = this.isPartialBlockedDate(selectedDate)
        if (this.partialBlockedDates.length > 0 && partialBlockedDate) {
          startTime = partialBlockedDate.startTime
          endTime = partialBlockedDate.endTime
        }
        const allTimes = this.globalState.arrTimes
        if (startTime == null && endTime == null) {
          for (let key in allTimes) {
            const time = allTimes[key]
            if (this.checkAvailableTime(selectedDate, time, endTime)) {
              availableTimes.push(time)
            }
          }
        } else {
          for (let key in allTimes) {
            const time = allTimes[key]
            if (time >= startTime && time <= endTime && this.checkAvailableTime(selectedDate, time, endTime)) {
              availableTimes.push(time)
            }
          }
        }
        return availableTimes
      },
      checkAvailableTime (selectedDate, startTime, availableEndTime) {
        let result = true
        if (this.professionalCalendarJobs) {
          const hours = Number(this.workHours) + 0.5
          const endTime = moment(selectedDate + ' ' + startTime).add(hours, 'hours').format('HH:mm')
          for (let job of this.professionalCalendarJobs) {
            if (startTime >= job.start_time && startTime <= job.end_time) {
              result = false
              break
            } else {
              const minutes = this.parseTime(startTime) - this.parseTime(job.end_time)
              if (startTime < job.start_time && endTime >= job.start_time) {
                result = false
                break
              } else if (startTime > job.end_time && minutes < 60) {
                result = false
                break
              }
            }
          }
        }
        // Check available end time comparing with duration if the selected date is partial blocked date
        if (result === true && availableEndTime) {
          const minutes = this.parseTime(availableEndTime) - this.parseTime(startTime)
          const endMinute = 60 * Number(this.workHours)
          if (minutes < endMinute) {
            result = false
          }
        }
        return result
      },
      async fetchProfessionalCalendar () {
        let response = await axios.get('professional/calendars', {
          params: {
            professional_id: this.professionalId,
            start_date: moment().format('YYYY-MM-DD'),
            end_date: null
          }
        })
        if (!response.data.error) {
          this.fetchSoldOutDates()
          // Blocked dates
          const blockedDates = response.data.blocked_dates
          for (let key in blockedDates) {
            const bDate = blockedDates[key]
            if (bDate.blocked_type === 'full') {
              this.fullBlockedDates.push(bDate.date)
            } else if (bDate.blocked_type === 'partial') {
              this.partialBlockedDates.push(bDate)
            }
          }
          // Cleaning dates
          const cleaningDates = response.data.cleaning_dates
          for (let key in cleaningDates) {
            const cDate = cleaningDates[key].date
            this.cleaningDates.push(cDate)
          }
        }
        this.initDatePicker()
      },
      async fetchProfessionalCalendarJobs (index, selectedDate, datePicker) {
        let response = await axios.get('professional/calendars/jobs', {
          params: {
            professional_id: this.professionalId,
            cleaning_date: selectedDate
          }
        })
        this.professionalCalendarJobs = null
        if (!response.data.error) {
          this.professionalCalendarJobs = response.data.calendar_jobs
        }
        this.availableTimes[index] = this.getAvailableTimes(selectedDate)
        if (this.availableTimes[index].length > 0) {
          this.selectedDates = datePicker
        } else {
          // alert(i18n.t('message.alert.no_available_service'))
          const date = datePicker[index]
          this.datePicker.data('datepicker').removeDate(date)
        }
      },
      async fetchSoldOutDates () {
        let response = await axios.get('professional/calendars/sold-out', {
          params: {
            professional_id: this.professionalId,
            start_date: moment().format('YYYY-MM-DD'),
            work_hour: this.workHours
          }
        })
        this.fullyBookedDates = []
        if (!response.data.error) {
          this.fullyBookedDates = response.data.sold_out_dates
          this.initDatePicker()
        }
      },
      updateSelectedDates (datePicker) {
        let tmp = {}
        for (let key in datePicker) {
          const date = moment(datePicker[key]).format('YYYY-MM-DD')
          if (this.mapDatesTimes[date]) {
            tmp[date] = this.mapDatesTimes[date]
          }
        }
        this.mapDatesTimes = { ...tmp }
        this.$emit('updateData', this.mapDatesTimes)
      },
      initDatePicker () {
        const minDate = moment().add(1, 'days')
        this.datePicker = window.$('.professionalMultiDatesCalendar').datepicker({
          language: this.language,
          minDate: minDate.toDate(),
          multipleDates: this.maxNum,
          onSelect: (fmDate, datePicker) => {
            this.numPackageSelectedDates = datePicker.length
            this.$emit('updateNumPackageSelectedDates', this.numPackageSelectedDates)
            // Need to check fmDate !== null
            // Don't change condition below
            if (fmDate !== null) {
              const arrayDates = fmDate.split(',')
              // Case: select
              if (arrayDates.length > this.selectedDates.length) {
                const selectedDate = arrayDates[arrayDates.length - 1]
                const index = arrayDates.indexOf(selectedDate)
                this.fetchProfessionalCalendarJobs(index, selectedDate, datePicker)
              } else { // Case: deselect
                for (let i = 0; i < this.selectedDates.length; i++) {
                  const fmDate = moment(this.selectedDates[i]).format('YYYY-MM-DD')
                  if (!arrayDates.includes(fmDate)) {
                    this.selectedDates.splice(i, 1)
                  }
                }
              }
            }
            this.updateSelectedDates(datePicker)
            // this.$emit('selectDate', selectedDate, datePicker)
          },
          onRenderCell: (date, cellType) => {
            const currentDate = date.getDate()
            const fmDate = moment(date).format('YYYY-MM-DD')
            if (cellType === 'day' && (this.fullBlockedDates.indexOf(fmDate) !== -1 || this.fullyBookedDates.indexOf(fmDate) !== -1)) {
              return {
                disabled: true
              }
            } else if (cellType === 'day' && this.cleaningDates.indexOf(fmDate) !== -1) {
              return {
                html: currentDate + '<span class="dp-note"></span>'
              }
            }
          }
        })
      }
    },
    mounted () {
      this.language = i18n.locale
      this.fetchProfessionalCalendar()

      this.$nextTick(() => {
        window.$.fn.datepicker.language['th'] = {
          days: [i18n.t('message.calendar.week.01'), i18n.t('message.calendar.week.02'), i18n.t('message.calendar.week.03'), i18n.t('message.calendar.week.04'), i18n.t('message.calendar.week.05'), i18n.t('message.calendar.week.06'), i18n.t('message.calendar.week.07')],
          daysShort: [i18n.t('message.calendar.week_short.01'), i18n.t('message.calendar.week_short.02'), i18n.t('message.calendar.week_short.03'), i18n.t('message.calendar.week_short.04'), i18n.t('message.calendar.week_short.05'), i18n.t('message.calendar.week_short.06'), i18n.t('message.calendar.week_short.07')],
          daysMin: [i18n.t('message.calendar.week_short.01'), i18n.t('message.calendar.week_short.02'), i18n.t('message.calendar.week_short.03'), i18n.t('message.calendar.week_short.04'), i18n.t('message.calendar.week_short.05'), i18n.t('message.calendar.week_short.06'), i18n.t('message.calendar.week_short.07')],
          months: [i18n.t('message.calendar.month.01'), i18n.t('message.calendar.month.02'), i18n.t('message.calendar.month.03'), i18n.t('message.calendar.month.04'), i18n.t('message.calendar.month.05'), i18n.t('message.calendar.month.06'), i18n.t('message.calendar.month.07'), i18n.t('message.calendar.month.08'), i18n.t('message.calendar.month.09'), i18n.t('message.calendar.month.10'), i18n.t('message.calendar.month.11'), i18n.t('message.calendar.month.12')],
          monthsShort: [i18n.t('message.calendar.month_short.01'), i18n.t('message.calendar.month_short.02'), i18n.t('message.calendar.month_short.03'), i18n.t('message.calendar.month_short.04'), i18n.t('message.calendar.month_short.05'), i18n.t('message.calendar.month_short.06'), i18n.t('message.calendar.month_short.07'), i18n.t('message.calendar.month_short.08'), i18n.t('message.calendar.month_short.09'), i18n.t('message.calendar.month_short.10'), i18n.t('message.calendar.month_short.11'), i18n.t('message.calendar.month_short.12')],
          today: i18n.t('message.calendar.today'),
          clear: 'Clear',
          dateFormat: 'yyyy-mm-dd',
          timeFormat: 'hh:ii aa',
          firstDay: 0
        }
      })
    }
  }
</script>


<style scoped>
  @import "~air-datepicker/dist/css/datepicker.min.css";
</style>



// WEBPACK FOOTER //
// src/components/booking/ProfessionalMultiDatesCalendar.vue